import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Building2, ExternalLink, Download, MapPin, AlertTriangle, CheckCircle2, FileText, Circle, Sparkles, Loader2, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const ERKLAERUNG_URL = "https://www.arbeitsagentur.de/datei/erklaerung-zum-beschaeftigungsverhaeltnis_ba047549.pdf";
const BEHOERDENFINDER_URL = "https://www.behoerdenfinder.de/";

export function RenewalGuidance({ emp, company }) {
  const { t } = useTranslation();
  const critical = emp.compliance_status === "CRITICAL";
  const docs = t("guide.docs", { returnObjects: true });
  const aw = t("guide.aw", { returnObjects: true });
  const docsArr = Array.isArray(docs) ? docs : [];
  const awArr = Array.isArray(aw) ? aw : [];
  const city = company?.city || company?.country || "";
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Ausländerbehörde " + city)}`;
  const FORMS = [
    { name: "Erklärung zum Beschäftigungsverhältnis (BA 047549)", url: ERKLAERUNG_URL, pdf: true, testid: "download-erklaerung" },
  ];
  const accent = critical ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40";
  const chip = critical ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  const REQ = { 1: "passport", 2: "permit", 5: "contract", 6: "declaration" };
  const [fulfilled, setFulfilled] = useState([]);
  useEffect(() => {
    if (emp?.id) api.get(`/employer/employees/${emp.id}/checklist`).then((r) => setFulfilled(r.data.fulfilled || [])).catch(() => {});
  }, [emp?.id]);

  const [abhPlz, setAbhPlz] = useState("");
  const [abhAddr, setAbhAddr] = useState(company?.city || "");
  const [abh, setAbh] = useState(null);
  const [abhLoading, setAbhLoading] = useState(false);
  useEffect(() => { if (company?.city && !abhAddr) setAbhAddr(company.city); }, [company?.city]);
  const findAbh = async () => {
    setAbhLoading(true); setAbh(null);
    try {
      const r = await api.post("/employer/find-abh", { plz: abhPlz, address: abhAddr, city: company?.city, employee_id: emp?.id });
      setAbh(r.data.abh);
    } catch { toast.error(t("abh.error")); }
    finally { setAbhLoading(false); }
  };
  const saveAbh = async () => {
    if (!abh) return;
    const label = [abh.name, abh.street, [abh.plz, abh.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    try { await api.put("/employer/company", { auslaenderbehoerde: label }); toast.success(t("abh.saved")); } catch { toast.error(t("abh.error")); }
  };

  return (
    <div className={`card-soft border ${accent} p-6`} data-testid="renewal-guidance">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${chip}`}><ClipboardCheck className="h-5 w-5" /></div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-bold text-navy">{t("guide.title")}</h3>
          <p className="mt-1 text-sm text-slate-600">{critical ? t("guide.subtitleCritical") : t("guide.subtitleAction")}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-navy"><Building2 className="h-4 w-4 text-cyan-dark" /> {t("guide.officeTitle")}</div>
          {company?.auslaenderbehoerde ? (
            <div className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm text-slate-700" data-testid="saved-office">
              <span className="text-xs text-slate-400">{t("guide.officeSaved")}:</span><br />{company.auslaenderbehoerde}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs text-slate-500">
              {t("guide.officeNotSet")} <Link to="/app/employer/company" className="font-semibold text-cyan-dark hover:underline">{t("guide.setOffice")}</Link>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="find-office-link"><Button size="sm" variant="outline" className="rounded-full text-xs"><MapPin className="mr-1 h-3.5 w-3.5" /> {t("guide.findOffice")}</Button></a>
          </div>

          <div className="mt-4 rounded-xl border border-cyan-200 bg-cyan/5 p-3" data-testid="abh-finder">
            <div className="flex items-center gap-2 text-sm font-bold text-navy"><Sparkles className="h-4 w-4 text-cyan-dark" /> {t("abh.title")}</div>
            <p className="mt-1 text-xs text-slate-500">{t("abh.hint")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Input value={abhPlz} onChange={(e) => setAbhPlz(e.target.value)} placeholder={t("abh.plz")} className="h-9 w-24" data-testid="abh-plz" />
              <Input value={abhAddr} onChange={(e) => setAbhAddr(e.target.value)} placeholder={t("abh.address")} className="h-9 min-w-[140px] flex-1" data-testid="abh-address" />
              <Button size="sm" onClick={findAbh} disabled={abhLoading || (!abhPlz && !abhAddr)} className="h-9 rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="abh-find-btn">{abhLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1 h-4 w-4" /> {t("abh.find")}</>}</Button>
            </div>
            {abh && (
              <div className="mt-3 rounded-lg border border-slate-100 bg-white p-3 text-sm" data-testid="abh-result">
                <div className="font-semibold text-navy">{abh.name}</div>
                <div className="text-xs text-slate-500">{[abh.street, [abh.plz, abh.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                  {abh.phone && <div>{t("abh.phone")}: {abh.phone}</div>}
                  {abh.email && <div>{t("abh.email")}: <a className="text-cyan-dark hover:underline" href={`mailto:${abh.email}`}>{abh.email}</a></div>}
                </div>
                {abh.note && <div className="mt-1 text-[11px] text-amber-600">{abh.note}</div>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {abh.website && <a href={abh.website} target="_blank" rel="noreferrer" data-testid="abh-website"><Button size="sm" variant="outline" className="h-8 rounded-full text-xs"><ExternalLink className="mr-1 h-3.5 w-3.5" /> {t("abh.website")}</Button></a>}
                  {abh.appointment_url && <a href={abh.appointment_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 rounded-full text-xs">{t("abh.appointment")}</Button></a>}
                  {abh.maps_url && <a href={abh.maps_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 rounded-full text-xs"><MapPin className="mr-1 h-3.5 w-3.5" /> {t("guide.findOffice")}</Button></a>}
                  <Button size="sm" onClick={saveAbh} className="h-8 rounded-full bg-navy text-xs text-white" data-testid="abh-save">{t("abh.save")}</Button>
                </div>
              </div>
            )}
          </div>

          <div className="mb-2 mt-6 flex items-center gap-2 text-sm font-bold text-navy"><FileText className="h-4 w-4 text-cyan-dark" /> {t("guide.formsTitle")}</div>
          <div className="space-y-2">
            {FORMS.map((form, i) => (
              <a key={i} href={form.url} target="_blank" rel="noreferrer" data-testid={form.testid}
                className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan/5 px-3 py-2 text-sm text-cyan-dark transition-colors hover:bg-cyan/10">
                {form.pdf ? <Download className="h-4 w-4 shrink-0" /> : <ExternalLink className="h-4 w-4 shrink-0" />}
                <span className="flex-1">{form.name}</span>
                {form.pdf && <span className="text-xs font-semibold">{t("guide.download")}</span>}
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-navy"><CheckCircle2 className="h-4 w-4 text-cyan-dark" /> {t("guide.docsTitle")}</div>
          <ul className="space-y-1.5">
            {docsArr.map((d, i) => {
              const done = REQ[i] && fulfilled.includes(REQ[i]);
              return (
                <li key={i} className={`flex items-start gap-2 text-sm ${done ? "text-emerald-700" : "text-slate-600"}`} data-testid={done ? `checklist-done-${REQ[i]}` : undefined}>
                  {done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : (REQ[i] ? <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" /> : <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan" />)}
                  <span className={done ? "line-through decoration-emerald-300" : ""}>{d}</span>
                </li>
              );
            })}
          </ul>
          {fulfilled.length > 0 && <p className="mt-2 text-[11px] text-emerald-600">{t("guide.autoDetected")}</p>}
          {emp.arbeitgeberwechsel && (
            <div data-testid="aw-checklist">
              <div className="mb-2 mt-4 flex items-center gap-2 text-sm font-bold text-navy"><AlertTriangle className="h-4 w-4 text-amber-500" /> {t("guide.awTitle")}</div>
              <ul className="space-y-1.5">
                {awArr.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /> {d}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <RenewalChat empId={emp.id} />
    </div>
  );
}

function RenewalChat({ empId }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid] = useState(() => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const endRef = useRef(null);
  const questions = t("rgChat.questions", { returnObjects: true });
  const qArr = Array.isArray(questions) ? questions : [];
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/employer/renewal-chat", { message: msg, session_id: sid, employee_id: empId });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch {
      toast.error(t("rgChat.error"));
      setMessages((m) => [...m, { role: "assistant", text: t("rgChat.error") }]);
    } finally { setLoading(false); }
  };
  return (
    <div className="mt-6 rounded-xl border border-cyan-200 bg-white p-4" data-testid="renewal-chat">
      <div className="flex items-center gap-2 text-sm font-bold text-navy"><MessageCircle className="h-4 w-4 text-cyan-dark" /> {t("rgChat.title")}</div>
      <p className="mt-1 text-xs text-slate-500">{t("rgChat.hint")}</p>
      {messages.length === 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="rgchat-questions">
          {qArr.map((q, i) => (<button key={i} type="button" onClick={() => send(q)} data-testid={`rgchat-q-${i}`} className="rounded-full border border-cyan-200 bg-cyan/5 px-3 py-1.5 text-left text-xs text-cyan-dark transition-colors hover:bg-cyan/10">{q}</button>))}
        </div>
      )}
      {messages.length > 0 && (
        <div className="mt-3 max-h-80 space-y-3 overflow-y-auto pr-1" data-testid="rgchat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-navy text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`rgchat-msg-${m.role}`}>{m.text}</div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="rounded-2xl bg-slate-100 px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div></div>}
          <div ref={endRef} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={t("rgChat.placeholder")} className="h-9" data-testid="rgchat-input" />
        <Button size="sm" onClick={() => send()} disabled={loading || !input.trim()} className="h-9 rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="rgchat-send"><Send className="h-4 w-4" /></Button>
      </div>
      <p className="mt-2 text-[10px] text-slate-400">{t("rgChat.disclaimer")}</p>
    </div>
  );
}
